import React, { FC } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useDocuments } from 'components/organisms/document/document-store';
import { DocumentResourceType, EditorModes } from 'data/documents';
import { useStatusField } from 'data/fields/hooks';
import {
  createDocumentTemplateResourceSectionAction,
  createDocumentTemplateTaskAction,
  deleteDocumentTemplateTaskAction,
  updateDocumentTemplateTaskAction,
} from 'data/templates/documents';
import { ProjectTemplateTask } from 'data/templates/projects';
import { CustomTaskSection } from './custom-task-section';
import { DefaultTaskSection } from './default-task-section';

const Container = styled.div`
  border-left: solid 1px var(--border-color);
  min-width: 400px;
  overflow-y: auto;
  padding: var(--spacing-05);
  width: 400px;
`;

export const DocumentTemplateTaskPane: FC<{
  docId: number;
  readOnly?: boolean;
}> = ({ docId, readOnly }) => {
  const dispatch = useDispatch();
  const statusField = useStatusField();
  const [state] = useDocuments();

  const handleNewTaskAdded = ({
    taskName,
    resourceSectionId,
    resourceType,
  }: {
    taskName: string;
    resourceSectionId: number;
    resourceType?: DocumentResourceType;
  }) => {
    dispatch(
      createDocumentTemplateTaskAction({
        docId,
        task: {
          taskName,
          fields: [
            {
              fieldId: statusField.fieldId,
              fieldValue: statusField.metaData?.default as number,
            },
          ],
        },
        resourceSectionId,
        resourceType,
      }),
    );

    return Promise.resolve();
  };

  const createResourceSection = (
    resourceSectionId?: number,
    resourceSectionName = '',
    resourceType: DocumentResourceType = DocumentResourceType.Default,
  ) => {
    dispatch(
      createDocumentTemplateResourceSectionAction({
        docId,
        resourceSectionId,
        resourceSectionName,
        resourceType,
      }),
    );
  };

  const handleAssigneeChange = (taskId, assignee, resourceSectionId) => {
    dispatch(
      updateDocumentTemplateTaskAction({
        docId,
        resourceSectionId,
        task: {
          taskId,
          assignee,
        },
      }),
    );
  };

  const onDeleteTask = (
    record: ProjectTemplateTask,
    resourceSectionId: number,
  ) => {
    dispatch(
      deleteDocumentTemplateTaskAction({
        docId,
        resourceSectionId,
        taskId: record.taskId,
      }),
    );

    const options = {
      taskId: `rl-task-${resourceSectionId}`,
    };

    if (state.allEditors) {
      state.allEditors[EditorModes.Private]?.execute(
        'removeTaskCommand',
        options,
      );
      state.allEditors[EditorModes.Public]?.execute(
        'removeTaskCommand',
        options,
      );
    }
  };

  return (
    <Container>
      <DefaultTaskSection
        docId={docId}
        readOnly={readOnly}
        createResourceSection={createResourceSection}
        handleAssigneeChange={handleAssigneeChange}
        handleNewTaskAdded={handleNewTaskAdded}
        onDeleteTask={onDeleteTask}
      />
      <CustomTaskSection
        docId={docId}
        readOnly={readOnly}
        createResourceSection={createResourceSection}
        handleAssigneeChange={handleAssigneeChange}
        handleNewTaskAdded={handleNewTaskAdded}
        onDeleteTask={onDeleteTask}
      />
    </Container>
  );
};
